import { apiFetch, useMockApi } from '@/lib/api/client'

export interface DashboardTopModel {
  modelId: string
  displayName: string
  categoryScore: number
}

export interface DashboardCategoryTopModels {
  categoryName: string
  models: DashboardTopModel[]
}

export interface DashboardWarning {
  categoryId?: string
  categoryName: string
  message: string
}

export interface DashboardSummary {
  openRouterConnected: boolean
  lastScanAt?: string
  lastScanStatus?: string
  totalModels: number
  freeModels: number
  cheapModels: number
  activeModels: number
  archivedModels: number
  openSessions: number
  topModelsByCategory: DashboardCategoryTopModels[]
  warnings: DashboardWarning[]
}

export interface DashboardApi {
  getDashboard(): Promise<DashboardSummary>
}

function mapDashboard(body: Record<string, unknown>): DashboardSummary {
  const topModels = (body.top_models_by_category as Array<Record<string, unknown>> | undefined) ?? []
  const warnings = (body.warnings as Array<Record<string, unknown>> | undefined) ?? []

  return {
    openRouterConnected: Boolean(body.openrouter_connected),
    lastScanAt: body.last_scan_at as string | undefined,
    lastScanStatus: body.last_scan_status as string | undefined,
    totalModels: Number(body.total_models ?? 0),
    freeModels: Number(body.free_models ?? 0),
    cheapModels: Number(body.cheap_models ?? 0),
    activeModels: Number(body.active_models ?? 0),
    archivedModels: Number(body.archived_models ?? 0),
    openSessions: Number(body.open_sessions ?? 0),
    topModelsByCategory: topModels.map((category) => ({
      categoryName: String(category.category_name ?? ''),
      models: ((category.models as Array<Record<string, unknown>> | undefined) ?? []).map((model) => ({
        modelId: String(model.model_id ?? ''),
        displayName: String(model.display_name ?? ''),
        categoryScore: Number(model.category_score ?? 0),
      })),
    })),
    warnings: warnings.map((warning) => ({
      categoryId: warning.category_id ? String(warning.category_id) : undefined,
      categoryName: String(warning.category_name ?? ''),
      message: String(warning.message ?? ''),
    })),
  }
}

const MOCK_DASHBOARD: DashboardSummary = {
  openRouterConnected: true,
  lastScanAt: new Date().toISOString(),
  lastScanStatus: 'completed',
  totalModels: 347,
  freeModels: 89,
  cheapModels: 124,
  activeModels: 210,
  archivedModels: 137,
  openSessions: 3,
  topModelsByCategory: [
    {
      categoryName: 'Coding',
      models: [
        { modelId: 'model-1', displayName: 'GPT-4o', categoryScore: 0.912 },
        { modelId: 'model-2', displayName: 'Claude 3.5', categoryScore: 0.88 },
      ],
    },
  ],
  warnings: [
    {
      categoryId: 'cat-research',
      categoryName: 'Research',
      message: 'category_limit_exceeded_due_to_pinned_models',
    },
  ],
}

class MockDashboardApi implements DashboardApi {
  async getDashboard(): Promise<DashboardSummary> {
    return structuredClone(MOCK_DASHBOARD)
  }
}

class HttpDashboardApi implements DashboardApi {
  async getDashboard(): Promise<DashboardSummary> {
    const body = await apiFetch<Record<string, unknown>>('/api/v1/dashboard')
    return mapDashboard(body)
  }
}

export const dashboardApi: DashboardApi = useMockApi() ? new MockDashboardApi() : new HttpDashboardApi()
